/**
 * OKX 支付服务 - Admin Web
 * 
 * 提供运营商 OKX 加密货币充值功能（与 user-web 使用相同的 Edge Function）
 */

import { supabase } from '@/lib/supabase'

const API_BASE_URL = import.meta.env.VITE_SUPABASE_URL 
  ? `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`
  : 'http://localhost:54321/functions/v1'

export interface CreateOKXPaymentRequest {
  amount: number
  currency?: 'USDT' | 'BTC' | 'ETH'
  chain?: 'TRC20' | 'ERC20' | 'BSC'
}

export interface OKXPaymentOrder {
  orderId: string
  paymentUrl?: string
  qrCode: string
  depositAddress: string
  memo?: string
  tag?: string
  amount: number
  currency: string
  chain: string
  expiresAt: string
  status: 'pending' | 'paid' | 'expired' | 'failed'
}

export interface OKXPaymentResult {
  success: boolean
  error?: string
  data?: OKXPaymentOrder
}

/**
 * 获取认证令牌（从 localStorage 获取，admin-web 使用自定义认证）
 */
async function getAuthToken(): Promise<string | null> {
  try {
    // admin-web 使用 localStorage 存储 token
    const token = localStorage.getItem('access_token')
    
    if (!token) {
      console.error('No access_token found in localStorage')
      return null
    }
    
    console.log('Auth token retrieved from localStorage')
    return token
  } catch (error) {
    console.error('Error getting auth token:', error)
    return null
  }
}

/**
 * 创建 OKX 支付订单（与 user-web 使用相同的 Edge Function）
 */
export async function createOKXPayment(
  request: CreateOKXPaymentRequest
): Promise<OKXPaymentResult> {
  const token = await getAuthToken()
  
  console.log('Token status:', token ? 'Found' : 'Not found')
  
  if (!token) {
    return {
      success: false,
      error: '认证失败，请重新登录'
    }
  }
  
  return createOKXPaymentWithToken(request, token)
}

/**
 * 使用指定的 token 创建 OKX 支付订单
 */
async function createOKXPaymentWithToken(
  request: CreateOKXPaymentRequest,
  token: string
): Promise<OKXPaymentResult> {
  try {
    console.log('Calling OKX API:', `${API_BASE_URL}/okx-create-payment`)
    
    const response = await fetch(
      `${API_BASE_URL}/okx-create-payment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(request)
      }
    )
    
    console.log('OKX API response status:', response.status)
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      console.error('OKX API error:', errorData)
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`)
    }
    
    const data = await response.json()
    console.log('OKX API response:', data)
    
    if (!data.success) {
      throw new Error(data.message || '创建支付订单失败')
    }
    
    return {
      success: true,
      data: data.data
    }
  } catch (error) {
    console.error('Error creating OKX payment:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '创建支付订单失败，请稍后重试'
    }
  }
}

/**
 * 查询 OKX 支付订单状态
 */
export async function getOKXPaymentStatus(
  orderId: string
): Promise<OKXPaymentResult> {
  try {
    const { data, error } = await supabase
      .from('okx_payment_orders')
      .select('*')
      .eq('okx_order_id', orderId)
      .single()
    
    if (error || !data) {
      throw new Error('订单不存在')
    }
    
    return {
      success: true,
      data: {
        orderId: data.okx_order_id,
        paymentUrl: data.okx_payment_url || '',
        qrCode: data.metadata?.qrCode || '',
        depositAddress: data.okx_deposit_address || '',
        amount: parseFloat(data.amount),
        currency: data.currency,
        chain: data.chain,
        expiresAt: data.expired_at,
        status: data.status
      }
    }
  } catch (error) {
    console.error('Error getting OKX payment status:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : '查询订单状态失败'
    }
  }
}

const okxService = {
  createOKXPayment,
  getOKXPaymentStatus
}

export default okxService
